import React, { useRef } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import {
  FocusStatus,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  PositionInfo,
  useTableCreatorPositionInfo,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useGetNumberOfGroupShell } from "@FillInTableContainer/shared/hooks/useGetNumberOfGroupShell";
import { TableCreatorGroupShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isTableCustomTitle } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

export interface TableInfoProps {
  groupTableKey: DealLaScSection;
  tableKey: string | null;
  groupShellList: TableCreatorGroupShellList;
  groupTableIndex: number;
  tableIndex: number;
  tableCount: number;
}
interface UseHandleTableCreatorTableReturn {
  info: PositionInfo;
  focusStatus: FocusStatus;
  isFocused: boolean;
  isLastTable: boolean;
  isVisibleTableCustomTitle: boolean;
  tableWrapperRef: React.RefObject<HTMLDivElement>;
  isEnableEdit: boolean;
  focusEventToChangePositionInfo: (focusEnterType: FocusStatus) => void;
  numberOfGroupShellCount: (groupShellKey: string | null) => number;
}

export const useHandleDealLaScTableCreatorTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const {
    groupTableKey,
    tableKey,
    groupTableIndex,
    tableIndex,
    tableCount,
    groupShellList,
  } = tablePropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { data: articleInfo } = useArticleInfoQuery(
    Number(getPressIdFromURL()),
  );

  const isEnableEdit = articleInfo
    ? ENABLE_GROUP_TABLE_KEY_LIST[articleSection][groupTableKey].includes(
        articleInfo.draftArticleStatus,
      )
    : false;

  const { groupShellCount } = useGetNumberOfGroupShell();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const isLastTable = tableIndex === tableCount - 1;

  const isFocused =
    info.groupTableKey === groupTableKey &&
    info.tableKey === tableKey &&
    info.tableIndex === tableIndex &&
    isEnableEdit;

  const isVisibleTableCustomTitle =
    isTableCustomTitle(articleSection, groupTableKey) &&
    groupShellCount(groupShellList[0].groupShellKey, tableIndex) > 1;

  const numberOfGroupShellCount = (groupShellKey: string | null) =>
    groupShellCount(groupShellKey, tableIndex);

  const focusEventToChangePositionInfo = (focusEnterType: FocusStatus) => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey: null,
      groupShellIndex: null,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "table" });

    if (focusEnterType.type === "clear") {
      changeFocusStatus({ type: "clear", container: "table" });
    }
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  return {
    tableWrapperRef,
    info,
    focusStatus,
    isFocused,
    isLastTable,
    isVisibleTableCustomTitle,
    isEnableEdit,
    numberOfGroupShellCount,
    focusEventToChangePositionInfo,
  };
};
